import api, { API_ENDPOINTS } from './api';
import adminApi from './adminApi';
import { CourseStructureNodeDTO } from './adminCourseService';

export type { CourseStructureNodeDTO };

export interface StudentTopicProgressDTO {
  topicId: number;
  status: string;
  progressPercentage: number;
  startedAt?: string;
  completedAt?: string;
  lastAccessedAt?: string;
}

// Types matching backend DTOs
export interface CurriculumGroupDTO {
  id: number;
  name: string;
  description: string;
  displayOrder: number;
  totalTopics: number;
  completedTopics: number;
  inProgressTopics: number;
  notStartedTopics: number;
  progressPercentage: number;
  areaId?: number;
  areaName?: string;
  experienceOutcomes?: ExperienceOutcomeDTO[];
  estimatedTimeMinutes?: number; // Total estimated time for all topics
  courseType?: string; // Course type (SECONDARY_SCHOOL, etc.)
  deliveryType?: string; // Course delivery type: SELF_TUTORED, SCHOOL_TEACHER, PRIVATE_TUTOR
}

export interface ExperienceOutcomeDTO {
  id: number;
  code: string;
  description: string;
  level: string;
  displayOrder: number;
}

export interface TopicDTO {
  id: number;
  title: string;
  description: string;
  content: string;
  estimatedTimeMinutes: number;
  difficultyLevel: string;
  displayOrder: number;
  status: string;
  progressStatus?: string;
  progressPercentage?: number;
  startedAt?: string;
  completedAt?: string;
  lastAccessedAt?: string;
  relatedEoCodes?: string[];
  learningObjectives?: string[]; // Learning objectives from topic and Experience Outcomes
}

export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
}

/**
 * Get all curriculum groups (Level 2 - shown as "Subjects") for a course
 */
export const getCourseCurriculumGroups = async (courseId: number): Promise<CurriculumGroupDTO[]> => {
  try {
    const response = await api.get<ApiResponse<CurriculumGroupDTO[]>>(
      `${API_ENDPOINTS.CURRICULUM.COURSE_GROUPS}/${courseId}/groups`
    );
    return response.data.data;
  } catch (error: any) {
    console.error('Error fetching course curriculum groups:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch curriculum groups');
  }
};

/**
 * Get curriculum groups for a specific course view (e.g. Book View)
 */
export const getCourseCurriculumGroupsByView = async (courseId: number, viewId: number): Promise<CurriculumGroupDTO[]> => {
  try {
    // Pass viewId as query parameter. Backend can use it to filter/structure topics.
    // If backend ignores it, it returns the default view, which is acceptable fallback.
    const response = await api.get<ApiResponse<CurriculumGroupDTO[]>>(
      `${API_ENDPOINTS.CURRICULUM.COURSE_GROUPS}/${courseId}/groups`,
      { params: { viewId } }
    );
    return response.data.data;
  } catch (error: any) {
    console.error(`Error fetching curriculum groups for view ${viewId}:`, error);
    throw new Error(error.response?.data?.message || 'Failed to fetch view curriculum groups');
  }
};

/**
 * Get all topics for a specific curriculum group
 */
export const getGroupTopics = async (courseId: number, groupId: number): Promise<TopicDTO[]> => {
  try {
    const response = await api.get<ApiResponse<TopicDTO[]>>(
      `${API_ENDPOINTS.CURRICULUM.GROUP_TOPICS}/${courseId}/groups/${groupId}/topics`
    );
    return response.data.data;
  } catch (error: any) {
    console.error('Error fetching group topics:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch topics');
  }
};

/**
 * Get individual topic details
 */
export const getTopicDetails = async (topicId: number, courseId?: number): Promise<TopicDTO> => {
  try {
    const url = courseId
      ? `${API_ENDPOINTS.CURRICULUM.TOPIC_DETAILS}/${topicId}?courseId=${courseId}`
      : `${API_ENDPOINTS.CURRICULUM.TOPIC_DETAILS}/${topicId}`;
    const response = await api.get<ApiResponse<TopicDTO>>(url);
    return response.data.data;
  } catch (error: any) {
    console.error('Error fetching topic details:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch topic details');
  }
};

/**
 * Get full curriculum hierarchy (for admin/teacher)
 */
export const getFullCurriculumHierarchy = async (subjectId: number): Promise<any> => {
  try {
    const response = await api.get<ApiResponse<any>>(
      `${API_ENDPOINTS.CURRICULUM.FULL_HIERARCHY}/${subjectId}/hierarchy`
    );
    return response.data.data;
  } catch (error: any) {
    console.error('Error fetching curriculum hierarchy:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch curriculum hierarchy');
  }
};

/**
 * Get Course Structure from Graph (Neo4j)
 * Connects directly to System Admin Service
 */
export const getCourseStructure = async (courseId: number, viewId?: number): Promise<CourseStructureNodeDTO[]> => {
  try {
    const response = await adminApi.get(
      `/api/admin/graph/courses/${courseId}/structure${viewId ? `?viewId=${viewId}` : ''}`
    );
    // adminApi returns plain list in response.data for this endpoint (based on controller analysis)
    return response.data;
  } catch (error: any) {
    console.error('Error fetching course structure from graph:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch course structure');
  }
};

/**
 * Get Course Progress (Status for each topic)
 */
export const getCourseProgress = async (courseId: number): Promise<StudentTopicProgressDTO[]> => {
  try {
    const response = await api.get<ApiResponse<StudentTopicProgressDTO[]>>(
      `${API_ENDPOINTS.CURRICULUM.COURSE_GROUPS}/${courseId}/progress`
    );
    return response.data.data;
  } catch (error: any) {
    // If endpoint not found or error, return empty list to allow fallback
    console.warn('Failed to fetch course progress:', error);
    return [];
  }
};

/**
 * Get Course Structure with Progress merged
 */
export const getCourseStructureWithProgress = async (courseId: number, viewId?: number): Promise<CourseStructureNodeDTO[]> => {
  try {
    const [structure, progressList] = await Promise.all([
      getCourseStructure(courseId, viewId),
      getCourseProgress(courseId)
    ]);

    // Create a map of progress for O(1) lookup
    const progressMap = new Map<number, StudentTopicProgressDTO>();
    progressList.forEach(p => {
      // Assuming Topic Ref ID in key. 
      // Structure Node has `refId` which points to Topic ID when nodeType is TOPIC_REF
      progressMap.set(p.topicId, p);
    });

    // Helper to recursively merge progress
    const mergeProgress = (nodes: CourseStructureNodeDTO[]): CourseStructureNodeDTO[] => {
      return nodes.map(node => {
        const newNode = { ...node };

        // If it's a topic ref, inject progress info
        // We might need to extend CourseStructureNodeDTO or add a temporary field
        // Since TS interface is flexible or we can cast, let's assume we can add `progress` field
        // or mapping status to node properties if UI expects it.
        // For now, let's Attach it as `studentProgress` property if permitted, or map status.

        if (node.nodeType === 'TOPIC_REF' && node.refId) {
          const progress = progressMap.get(node.refId);
          if (progress) {
            // @ts-ignore - Injecting dynamic property for UI
            newNode.progress = progress;
            // @ts-ignore
            newNode.status = progress.status;
          }
        }

        if (newNode.children) {
          newNode.children = mergeProgress(newNode.children);
        }
        return newNode;
      });
    };

    return mergeProgress(structure);

  } catch (error: any) {
    console.error('Error fetching structure with progress:', error);
    throw error;
  }
};


